import { api } from './client';
import type {
  KycSubmission, ReconExceptionRow, ReconSummary, RgLimit, RiskAlert, SelfExclusion,
  SupportCannedReply, SupportMemberHit, SupportMemberSnapshot, SupportStats, SupportTicket,
} from './types';
import type {
  FundistCategory, FundistGameRow, FundistProviderRow, FundistProvidersResponse,
  FundistReport, FundistSyncResult, FundistTransactionRow, FundistTxStatus, FundistTxType,
} from './types';
import type {
  AdminAccount, AdminUserRow, Activity, AffiliateOverview, AffiliateTree, AnalyticsKpis,
  AuditLog, Banner, BankCard, BareList, BetOrder, CommissionRecord, CustomerService,
  DashboardCounts, GamePerformanceResponse, GamePnl, GameIssue, GameType, HealthStatus,
  HomeSetting, IssueExposure, LedgerEntry, LoginResponse, OddsConfig, OrderDetail, OrderStatus,
  Paginated, PaymentMethod, PointMallOrder, PointMallProduct, RechargeOrder, SettingSchemaItem,
  SiteNotice, TimeseriesResponse, UserDetail, VipLevel, WalletBalance, WithdrawOrder,
} from './types';

/**
 * Typed wrappers over the admin API. The existing 25 routes power Phase A; the
 * modules below (analytics/members/finance-ext/games-ext/engagement/affiliate/
 * account/platform) hit the new backend modules and power Phase B.
 */

// ---- Phase A (existing surface) ------------------------------------------

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/api/admin/login', { email, password }, { skipAuthRedirect: true }),
};

export const dashboardApi = {
  counts: () => api.get<DashboardCounts>('/api/admin/dashboard'),
};

export const usersApi = {
  list: (params: { pageNo?: number; pageSize?: number; q?: string; status?: AdminUserRow['status'] }) =>
    api.get<Paginated<AdminUserRow>>('/api/admin/users', { params }),
  adjustBalance: (body: { userId: string; amount: number; remark?: string }) =>
    api.post<WalletBalance>('/api/admin/users/adjust-balance', body),
  ban: (body: { userId: string; ban: boolean }) =>
    api.post<{ id: string; status: string }>('/api/admin/users/ban', body),
};

export const financeApi = {
  withdrawals: (status?: OrderStatus) =>
    api.get<BareList<WithdrawOrder>>('/api/admin/withdrawals', { params: { status } }),
  reviewWithdrawal: (body: { orderNo: string; approve: boolean; remark?: string }) =>
    api.post<WithdrawOrder>('/api/admin/withdrawals/review', body),
  recharges: (status?: OrderStatus) =>
    api.get<BareList<RechargeOrder>>('/api/admin/recharges', { params: { status } }),
  confirmRecharge: (body: { orderNo: string; remark?: string }) =>
    api.post<RechargeOrder>('/api/admin/recharges/confirm', body),
  togglePaymentMethod: (body: { code: string; enabled: boolean }) =>
    api.post<PaymentMethod>('/api/admin/payment-methods/toggle', body),
  // Phase B — real ledger + queues + method CRUD (new backend module)
  ledger: (params: {
    userId?: string; type?: string; walletType?: string; referenceType?: string;
    dateFrom?: string; dateTo?: string; pageNo?: number; pageSize?: number;
  }) => api.get<Paginated<LedgerEntry>>('/api/admin/ledger', { params }),
  paymentMethods: () => api.get<BareList<PaymentMethod>>('/api/admin/payment-methods'),
  upsertPaymentMethod: (body: Partial<PaymentMethod> & { code: string; name: string; type: string }) =>
    api.post<PaymentMethod>('/api/admin/payment-methods', body),
  deletePaymentMethod: (code: string) => api.del<PaymentMethod>(`/api/admin/payment-methods/${code}`),
  withdrawalsPaged: (params: Record<string, string | number | undefined>) =>
    api.get<Paginated<WithdrawOrder>>('/api/admin/finance/withdrawals', { params }),
  rechargesPaged: (params: Record<string, string | number | undefined>) =>
    api.get<Paginated<RechargeOrder>>('/api/admin/finance/recharges', { params }),
  orderDetail: (orderNo: string) => api.get<OrderDetail>(`/api/admin/finance/orders/${orderNo}`),
};

export const gamesApi = {
  bets: (params: { pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<BetOrder>>('/api/admin/bets', { params }),
  issues: (params: { pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<GameIssue>>('/api/admin/issues', { params }),
  forceDraw: (body: { issueId?: string; gameCode?: string; typeId?: number; number?: string }) =>
    api.post<{ ok: boolean; issueId?: string; gameCode?: string }>('/api/admin/games/force-draw', body),
  // Phase B — config + odds + exposure + pnl (new backend module)
  list: () => api.get<BareList<GameType>>('/api/admin/games'),
  update: (code: string, body: Partial<GameType>) =>
    api.patch<GameType>(`/api/admin/games/${code}`, body),
  odds: (code: string) => api.get<BareList<OddsConfig>>(`/api/admin/games/${code}/odds`),
  upsertOdds: (code: string, body: { playCode: string; odds: number; enabled?: boolean; meta?: unknown }) =>
    api.post<OddsConfig>(`/api/admin/games/${code}/odds`, body),
  updateOdds: (id: string, body: { odds?: number; enabled?: boolean; meta?: unknown }) =>
    api.patch<OddsConfig>(`/api/admin/odds/${id}`, body),
  deleteOdds: (id: string) => api.del<OddsConfig>(`/api/admin/odds/${id}`),
  exposure: (issueId: string) => api.get<IssueExposure>(`/api/admin/issues/${issueId}/exposure`),
  pnl: (params: { gameCode?: string; from?: string; to?: string }) =>
    api.get<BareList<GamePnl>>('/api/admin/games/pnl', { params }),
  betsSearch: (params: Record<string, string | number | undefined>) =>
    api.get<Paginated<BetOrder>>('/api/admin/games/bets/search', { params }),
};

export const auditApi = {
  list: (params: { pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<AuditLog>>('/api/admin/audit-logs', { params }),
  search: (params: Record<string, string | number | undefined>) =>
    api.get<Paginated<AuditLog & { admin?: AdminAccount }>>('/api/admin/audit-logs/search', { params }),
};

export const contentApi = {
  banners: () => api.get<BareList<Banner>>('/api/admin/banners'),
  upsertBanner: (body: Partial<Banner> & { imageUrl: string }) =>
    api.post<Banner>('/api/admin/banners', body),
  deleteBanner: (id: string) => api.del<Banner>(`/api/admin/banners/${id}`),
  reorderBanners: (items: { id: string; sortOrder: number }[]) =>
    api.post<{ ok: boolean }>('/api/admin/banners/reorder', { items }),

  settings: () => api.get<BareList<HomeSetting>>('/api/admin/settings'),
  setSetting: (body: { key: string; value: unknown }) =>
    api.post<HomeSetting>('/api/admin/settings', body),
  settingsSchema: () => api.get<BareList<SettingSchemaItem>>('/api/admin/settings/schema'),

  customerService: () => api.get<BareList<CustomerService>>('/api/admin/customer-service'),
  upsertCustomerService: (body: Partial<CustomerService> & { name: string }) =>
    api.post<CustomerService>('/api/admin/customer-service', body),
  deleteCustomerService: (id: string) => api.del<CustomerService>(`/api/admin/customer-service/${id}`),

  notices: () => api.get<BareList<SiteNotice>>('/api/admin/notices'),
  createNotice: (body: { title: string; content: string; type?: string }) =>
    api.post<SiteNotice>('/api/admin/notices', body),
  deleteNotice: (id: string) => api.del<SiteNotice>(`/api/admin/notices/${id}`),
  broadcast: (body: { audience: 'all' | 'user'; userId?: string; title: string; content: string; type?: string }) =>
    api.post<SiteNotice>('/api/admin/notifications/broadcast', body),
};

// ---- Phase B: new modules -------------------------------------------------

export const analyticsApi = {
  kpis: (params: { from?: string; to?: string }) =>
    api.get<AnalyticsKpis>('/api/admin/analytics/kpis', { params }),
  timeseries: (params: { metric: string; from?: string; to?: string; interval?: string }) =>
    api.get<TimeseriesResponse>('/api/admin/analytics/timeseries', { params }),
  gamePerformance: (params: { from?: string; to?: string }) =>
    api.get<GamePerformanceResponse>('/api/admin/analytics/game-performance', { params }),
};

export const membersApi = {
  detail: (id: string) => api.get<UserDetail>(`/api/admin/users/${id}`),
  ledger: (id: string, params: { type?: string; walletType?: string; pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<LedgerEntry>>(`/api/admin/users/${id}/ledger`, { params }),
  bets: (id: string, params: { status?: string; pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<BetOrder>>(`/api/admin/users/${id}/bets`, { params }),
  recharges: (id: string, params: { status?: string; pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<RechargeOrder>>(`/api/admin/users/${id}/recharges`, { params }),
  withdrawals: (id: string, params: { status?: string; pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<WithdrawOrder>>(`/api/admin/users/${id}/withdrawals`, { params }),
  bankCards: (id: string) => api.get<BareList<BankCard>>(`/api/admin/users/${id}/bank-cards`),
  update: (id: string, body: Partial<{ nickname: string; email: string; phone: string; language: string; vipLevel: number }>) =>
    api.patch<unknown>(`/api/admin/users/${id}`, body),
  setStatus: (id: string, body: { status: 'ACTIVE' | 'BANNED' | 'DELETED'; reason?: string }) =>
    api.post<unknown>(`/api/admin/users/${id}/status`, body),
};

export const engagementApi = {
  activities: (params: { type?: string; enabled?: boolean; pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<Activity>>('/api/admin/activities', { params }),
  activity: (id: string) => api.get<Activity>(`/api/admin/activities/${id}`),
  upsertActivity: (body: Partial<Activity> & { type: string; title: string }) =>
    api.post<Activity>('/api/admin/activities', body),
  deleteActivity: (id: string) => api.del<Activity>(`/api/admin/activities/${id}`),
  toggleActivity: (id: string, enabled: boolean) =>
    api.patch<Activity>(`/api/admin/activities/${id}/enabled`, { enabled }),

  vipLevels: () => api.get<BareList<VipLevel>>('/api/admin/vip-levels'),
  upsertVipLevel: (body: Partial<VipLevel> & { level: number; name: string }) =>
    api.post<VipLevel>('/api/admin/vip-levels', body),
  deleteVipLevel: (id: string) => api.del<VipLevel>(`/api/admin/vip-levels/${id}`),

  products: () => api.get<BareList<PointMallProduct>>('/api/admin/point-mall/products'),
  upsertProduct: (body: Partial<PointMallProduct> & { name: string; pointsCost: number }) =>
    api.post<PointMallProduct>('/api/admin/point-mall/products', body),
  deleteProduct: (id: string) => api.del<PointMallProduct>(`/api/admin/point-mall/products/${id}`),
  setStock: (id: string, stock: number) =>
    api.patch<PointMallProduct>(`/api/admin/point-mall/products/${id}/stock`, { stock }),
  toggleProduct: (id: string, enabled: boolean) =>
    api.patch<PointMallProduct>(`/api/admin/point-mall/products/${id}/enabled`, { enabled }),
  orders: (params: { status?: string; pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<PointMallOrder>>('/api/admin/point-mall/orders', { params }),
  setOrderStatus: (orderNo: string, status: string) =>
    api.post<PointMallOrder>(`/api/admin/point-mall/orders/${orderNo}/status`, { status }),
  cancelOrder: (orderNo: string, reason?: string) =>
    api.post<PointMallOrder>(`/api/admin/point-mall/orders/${orderNo}/cancel`, { reason }),
};

export const affiliateApi = {
  overview: (params: { from?: string; to?: string }) =>
    api.get<AffiliateOverview>('/api/admin/affiliate/overview', { params }),
  tree: (userId: string) => api.get<AffiliateTree>('/api/admin/affiliate/tree', { params: { userId } }),
  commissions: (params: Record<string, string | number | undefined>) =>
    api.get<Paginated<CommissionRecord>>('/api/admin/commissions', { params }),
  config: () =>
    api.get<{
      config: { levels: { level: number; rate: number; enabled?: boolean }[] };
      isDefault?: boolean;
      note?: string;
    }>('/api/admin/commission-config'),
  setConfig: (levels: { level: number; rate: number; enabled?: boolean }[]) =>
    api.post<unknown>('/api/admin/commission-config', { levels }),
};

export const accountApi = {
  me: () => api.get<AdminAccount>('/api/admin/me'),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    api.post<unknown>('/api/admin/change-password', body),
  admins: () => api.get<BareList<AdminAccount>>('/api/admin/admins'),
  createAdmin: (body: { email: string; name?: string; password: string; role: string }) =>
    api.post<AdminAccount>('/api/admin/admins', body),
  updateAdmin: (id: string, body: { name?: string; role?: string; status?: string }) =>
    api.patch<AdminAccount>(`/api/admin/admins/${id}`, body),
  resetAdminPassword: (id: string, newPassword: string) =>
    api.post<unknown>(`/api/admin/admins/${id}/reset-password`, { newPassword }),
};

export const platformApi = {
  health: () => api.get<HealthStatus>('/api/admin/health'),
};

// ---- Phase C: Compliance --------------------------------------------------

export const kycApi = {
  list: (params: { status?: string; pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<KycSubmission>>('/api/admin/kyc', { params }),
  review: (id: string, body: { approve: boolean; reason?: string }) =>
    api.post<KycSubmission>(`/api/admin/kyc/${id}/review`, body),
  stats: () => api.get<{ pending: number; approved: number; rejected: number }>('/api/admin/kyc/stats'),
};

export const rgApi = {
  limits: (params: { userId?: string; pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<RgLimit>>('/api/admin/rg/limits', { params }),
  setLimit: (body: { userId: string; type: string; value: number; note?: string }) =>
    api.post<RgLimit>('/api/admin/rg/limits', body),
  removeLimit: (id: string) => api.del<RgLimit>(`/api/admin/rg/limits/${id}`),
  exclusions: (params: { status?: string; pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<SelfExclusion>>('/api/admin/rg/exclusions', { params }),
  addExclusion: (body: { userId: string; endAt?: string; reason?: string }) =>
    api.post<SelfExclusion>('/api/admin/rg/exclusions', body),
  revokeExclusion: (id: string) =>
    api.post<SelfExclusion>(`/api/admin/rg/exclusions/${id}/revoke`, {}),
  stats: () => api.get<{ activeExclusions: number; activeLimits: number }>('/api/admin/rg/stats'),
};

export const riskApi = {
  alerts: (params: { status?: string; severity?: string; type?: string; pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<RiskAlert>>('/api/admin/risk/alerts', { params }),
  setStatus: (id: string, body: { status: string; note?: string }) =>
    api.post<RiskAlert>(`/api/admin/risk/alerts/${id}/status`, body),
  scan: () => api.post<{ created: number; scanned: number }>('/api/admin/risk/scan', {}),
  stats: () => api.get<{ open: number; bySeverity: Record<string, number> }>('/api/admin/risk/stats'),
};

export const reconApi = {
  exceptions: (params: { kind?: string; pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<ReconExceptionRow>>('/api/admin/reconciliation/exceptions', { params }),
  summary: () => api.get<ReconSummary>('/api/admin/reconciliation/summary'),
};

// ---- Fundist (SoftGamings) casino aggregator ------------------------------
// Contract: api/docs/FUNDIST_INTEGRATION.md §7. Mutations are @Roles(SUPER_ADMIN, ADMIN).

export const fundistApi = {
  providers: () => api.get<FundistProvidersResponse>('/api/admin/fundist/providers'),
  patchProvider: (
    merchantId: string,
    body: { enabled: boolean; sortOrder?: number; category?: FundistCategory },
  ) => api.patch<FundistProviderRow>(`/api/admin/fundist/providers/${merchantId}`, body),
  games: (params: { merchantId?: string; q?: string; pageNo?: number; pageSize?: number }) =>
    api.get<Paginated<FundistGameRow>>('/api/admin/fundist/games', { params }),
  patchGame: (fundistId: string, body: { enabled: boolean }) =>
    api.patch<FundistGameRow>(`/api/admin/fundist/games/${fundistId}`, body),
  sync: () => api.post<FundistSyncResult>('/api/admin/fundist/sync', {}),
  transactions: (params: {
    userId?: string; actionId?: string; roundId?: string; gameId?: string;
    type?: FundistTxType; status?: FundistTxStatus;
    from?: string; to?: string; pageNo?: number; pageSize?: number;
  }) => api.get<Paginated<FundistTransactionRow>>('/api/admin/fundist/transactions', { params }),
  report: (params: { from?: string; to?: string }) =>
    api.get<FundistReport>('/api/admin/fundist/report', { params }),
};

export const supportApi = {
  stats: () => api.get<SupportStats>('/api/admin/support/stats'),
  tickets: (params: {
    status?: string;
    category?: string;
    q?: string;
    assignedAdminId?: string;
    pageNo?: number;
    pageSize?: number;
  }) => api.get<Paginated<SupportTicket>>('/api/admin/support/tickets', { params }),
  ticket: (id: string) => api.get<SupportTicket>(`/api/admin/support/tickets/${id}`),
  createTicket: (body: { userId?: string; subject: string; content: string; category?: string }) =>
    api.post<SupportTicket>('/api/admin/support/tickets', body),
  reply: (id: string, content: string) =>
    api.post<SupportTicket>(`/api/admin/support/tickets/${id}/reply`, { content }),
  assign: (id: string, adminId?: string) =>
    api.post<SupportTicket>(`/api/admin/support/tickets/${id}/assign`, { adminId }),
  setStatus: (id: string, status: string) =>
    api.post<SupportTicket>(`/api/admin/support/tickets/${id}/status`, { status }),
  members: (q: string) => api.get<SupportMemberHit[]>('/api/admin/support/members', { params: { q } }),
  snapshot: (id: string) => api.get<SupportMemberSnapshot>(`/api/admin/support/members/${id}`),
  addNote: (id: string, body: string) =>
    api.post<SupportMemberSnapshot['notes'][number]>(`/api/admin/support/members/${id}/notes`, { body }),
  canned: () => api.get<SupportCannedReply[]>('/api/admin/support/canned'),
};
